import { Camera, Search, X, Clock } from 'lucide-react';
import { useRef, useEffect } from 'react';
import { cn } from '../lib/utils';
import type { Mode } from '../lib/types';
import { Input } from './ui/input';
import { Button } from './ui/button';

interface BottomNavProps {
  query: string;
  onQueryChange: (query: string) => void;
  onClear: () => void;
  isLoading: boolean;
  interpretation?: string;
  mode: Mode;
  onModeChange: (mode: Mode) => void;
  buyPercent: number;
  onBuyPercentChange: (percent: number) => void;
  onOpenHistory?: () => void;
  onScanOpen?: () => void;
}

export function BottomNav({
  query,
  onQueryChange,
  onClear,
  isLoading,
  interpretation,
  mode,
  onModeChange,
  buyPercent,
  onBuyPercentChange,
  onOpenHistory,
  onScanOpen,
}: BottomNavProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const presets = [50, 60, 70, 80];

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="bottom-nav">
      {/* Search interpretation (shows above search bar when typing) */}
      {interpretation && query.length >= 2 && (
        <div className="px-4 pb-2">
          <p className="text-xs text-muted-foreground">{interpretation}</p>
        </div>
      )}

      {/* Search input row */}
      <div className="px-4 pb-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              {isLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-outline border-t-primary" />
              ) : (
                <Search className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <Input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search cards..."
              className="h-11 pl-10 pr-10 text-sm"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
            {query && (
              <button
                onClick={onClear}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                <X className="h-5 w-5 text-muted-foreground hover:text-foreground transition-colors" />
              </button>
            )}
          </div>
          {onScanOpen && (
            <Button
              onClick={onScanOpen}
              variant="outline"
              size="icon"
              className="h-11 w-11 flex-shrink-0"
              aria-label="Scan a card"
              title="Scan a card"
            >
              <Camera className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Mode toggle + buy percent presets row */}
      <div className="flex items-center justify-between gap-3 px-4 pb-4">
        {/* Mode toggle — MD3 segmented button (Secondary Container fill on
            the selected segment; no per-mode hue-coding). */}
        <div className="flex rounded-full bg-muted p-1">
          <button
            onClick={() => onModeChange('sell')}
            className={cn(
              'rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 ease-emphasized-decelerate',
              mode === 'sell'
                ? 'bg-secondary text-secondary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Sell
          </button>
          <button
            onClick={() => onModeChange('buy')}
            className={cn(
              'rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 ease-emphasized-decelerate',
              mode === 'buy'
                ? 'bg-secondary text-secondary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            Buy
          </button>
        </div>

        {/* Buy percent presets (always visible, but only active in buy mode) */}
        <div className="flex items-center gap-1">
          {presets.map((percent) => (
            <button
              key={percent}
              onClick={() => {
                onModeChange('buy');
                onBuyPercentChange(percent);
              }}
              className={cn(
                'rounded-full px-2 py-1.5 text-xs font-medium transition-all duration-200 ease-emphasized-decelerate',
                mode === 'buy' && buyPercent === percent
                  ? 'bg-secondary text-secondary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              )}
            >
              {percent}%
            </button>
          ))}

          {/* History button */}
          {onOpenHistory && (
            <button
              onClick={onOpenHistory}
              className="ml-1 rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              title="Session History"
            >
              <Clock className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
